import { CloseOutlined } from '@ant-design/icons';
import Tooltip from 'antd/es/tooltip';
import type { ReactNode } from 'react';
import { useState } from 'react';

import { formatLongDate } from 'utils/dayjs';

import type { Webhook } from 'api';
import { Restricted } from 'containers/Restricted';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { SecondaryButton } from '@atoms/buttons/SecondaryButton';
import { TertiaryButton } from '@atoms/buttons/TertiaryButton';

import { CopyToClipboardButton } from '@molecules/CopyToClipboardButton';

import {
  AUTH_SCHEMA_DISABLED_ERROR,
  WEBHOOK_DETAILS_ACTIVATE_TEST_ID,
  WEBHOOK_DETAILS_BUSINESS_UNIT_TEST_ID,
  WEBHOOK_DETAILS_CLOSE_TEST_ID,
  WEBHOOK_DETAILS_CREATED_AT_TEST_ID,
  WEBHOOK_DETAILS_CREATED_BY_TEST_ID,
  WEBHOOK_DETAILS_DEACTIVATE_TEST_ID,
  WEBHOOK_DETAILS_EDIT_TEST_ID,
  WEBHOOK_DETAILS_ENDPOINT_TEST_ID,
  WEBHOOK_DETAILS_EVENT_TEST_ID,
  WEBHOOK_DETAILS_READONLY_NOTE_TEST_ID,
  WEBHOOK_DETAILS_STATUS_TEST_ID,
  WEBHOOK_DETAILS_TEST_ID,
  WEBHOOK_DETAILS_UUID_TEST_ID,
  WEBHOOK_MANAGED_BY_TENANT,
  webhookCreatorLabel
} from './constants';

import { useAuthenticationKeys } from '../../hooks/useAuthenticationKeys';
import { ToggleWebhookStatusModal } from '../ToggleWebhookStatusModal';
import { WebhookStatusBadge } from '../WebhookStatusBadge';

type ToggleMode = 'activate' | 'deactivate';

type FieldProps = {
  label: string;
  testId?: string;
  children: ReactNode;
};

const Field = ({ label, testId, children }: FieldProps) => (
  <div className="flex flex-col gap-1" data-testid={testId}>
    <span className="text-sm text-content-secondary">{label}</span>
    {children}
  </div>
);

type PlainFieldProps = {
  label: string;
  value: string;
  testId: string;
};

const PlainField = ({ label, value, testId }: PlainFieldProps) => (
  <Field label={label} testId={testId}>
    <span className="text-base font-medium text-content-primary">{value}</span>
  </Field>
);

type CopyChipProps = {
  text?: string | null;
  testId: string;
};

const CopyChip = ({ text, testId }: CopyChipProps) => (
  <div data-testid={testId}>
    <CopyToClipboardButton
      text={text}
      className="flex w-full translate-x-0 items-center justify-between gap-2 rounded-lg bg-surface-muted p-2 font-mono text-sm font-normal text-content-primary hover:bg-surface-neutral"
    />
  </div>
);

type WebhookDetailsProps = {
  webhook: Webhook;
  onClose: () => void;
  onEdit: () => void;
};

export const WebhookDetails = ({
  webhook,
  onClose,
  onEdit
}: WebhookDetailsProps) => {
  const [toggle, setToggle] = useState<ToggleMode | null>(null);
  const { keys: authKeys } = useAuthenticationKeys();
  const authSchemaDisabled = !authKeys.find(
    (key) => key.uuid === webhook.auth_uuid
  )?.enabled;

  return (
    <div
      data-testid={WEBHOOK_DETAILS_TEST_ID}
      className="cool-shadow flex min-h-0 flex-col rounded-lg border border-border-muted bg-white"
    >
      <div className="flex items-center justify-between rounded-t-lg bg-white px-6 py-4">
        <span className="text-lg font-bold text-content-primary">Details</span>
        <TertiaryButton
          data-testid={WEBHOOK_DETAILS_CLOSE_TEST_ID}
          aria-label="Close details"
          icon={<CloseOutlined className="text-base" />}
          onClick={onClose}
        />
      </div>

      <div className="flex flex-col gap-4 px-6 pb-4">
        <Field label="Status" testId={WEBHOOK_DETAILS_STATUS_TEST_ID}>
          <WebhookStatusBadge active={webhook.active} />
        </Field>

        <Field label="Endpoint URL">
          <CopyChip
            text={webhook.destination_url}
            testId={WEBHOOK_DETAILS_ENDPOINT_TEST_ID}
          />
        </Field>

        <PlainField
          label="Business unit"
          value={webhook.business_unit_name}
          testId={WEBHOOK_DETAILS_BUSINESS_UNIT_TEST_ID}
        />

        <Field label="X-Auth-UUID">
          <CopyChip
            text={webhook.auth_uuid}
            testId={WEBHOOK_DETAILS_UUID_TEST_ID}
          />
        </Field>

        <Field label="Event">
          <CopyChip
            text={webhook.event}
            testId={WEBHOOK_DETAILS_EVENT_TEST_ID}
          />
        </Field>

        <PlainField
          label="Created by"
          value={webhookCreatorLabel(webhook.creator_type)}
          testId={WEBHOOK_DETAILS_CREATED_BY_TEST_ID}
        />
        <PlainField
          label="Date created"
          value={formatLongDate(webhook.created_at)}
          testId={WEBHOOK_DETAILS_CREATED_AT_TEST_ID}
        />
      </div>

      <div className="flex flex-col gap-3 border-t border-border-default px-6 py-4">
        {!webhook.editable ? (
          <p
            data-testid={WEBHOOK_DETAILS_READONLY_NOTE_TEST_ID}
            className="text-sm text-content-secondary"
          >
            {WEBHOOK_MANAGED_BY_TENANT}
          </p>
        ) : (
          <Restricted requiredPermission="developers:manage">
            {webhook.active ? (
              <>
                <SecondaryButton
                  danger
                  block
                  data-testid={WEBHOOK_DETAILS_DEACTIVATE_TEST_ID}
                  onClick={() => setToggle('deactivate')}
                >
                  Deactivate
                </SecondaryButton>
                <PrimaryButton
                  block
                  data-testid={WEBHOOK_DETAILS_EDIT_TEST_ID}
                  onClick={onEdit}
                >
                  Edit
                </PrimaryButton>
              </>
            ) : (
              <>
                <SecondaryButton
                  block
                  data-testid={WEBHOOK_DETAILS_EDIT_TEST_ID}
                  onClick={onEdit}
                >
                  Edit
                </SecondaryButton>
                <Tooltip
                  title={
                    authSchemaDisabled ? AUTH_SCHEMA_DISABLED_ERROR : undefined
                  }
                >
                  <span className="block w-full">
                    <PrimaryButton
                      block
                      data-testid={WEBHOOK_DETAILS_ACTIVATE_TEST_ID}
                      disabled={authSchemaDisabled}
                      onClick={() => setToggle('activate')}
                    >
                      Activate
                    </PrimaryButton>
                  </span>
                </Tooltip>
              </>
            )}
          </Restricted>
        )}
      </div>

      <ToggleWebhookStatusModal
        webhooks={[webhook]}
        mode={toggle ?? 'deactivate'}
        open={toggle !== null}
        onCancel={() => setToggle(null)}
        onConfirmed={() => setToggle(null)}
      />
    </div>
  );
};
